import * as E from 'fp-ts/Either';
import * as t from 'io-ts';

import {reportWarn} from './ioTsLogger';
import {shouldDisableSafeMode} from './safeTypesHelpers';

/** Returns a runtime type similar to t.partial that drops invalid values. See makeSafeArrayType(). */
export function makeSafePartialType<P extends t.Props>(props: P, name?: string) {
  const baseType = t.partial(props, name);

  return new t.PartialType(
    baseType.name,
    baseType.is,
    (unknownInput, context) => {
      // Default to the base implementation when strict mode is found in the context or if we're inside a union.
      const isStrict = shouldDisableSafeMode(context);
      if (isStrict) {
        return baseType.validate(unknownInput, context);
      }

      // "safe" implementation.
      const safeResult = t.UnknownRecord.validate(unknownInput, context);
      if (E.isLeft(safeResult)) {
        return safeResult;
      }

      const validatedValues: {
        [K in keyof P]?: t.TypeOf<P[K]>;
      } = {};

      Object.keys(props).forEach((key) => {
        if (!Object.prototype.hasOwnProperty.call(safeResult.right, key)) {
          return;
        }

        const valueCodec = props[key];
        const value = safeResult.right[key];
        const result = valueCodec.validate(value, t.appendContext(context, key, valueCodec, value));

        if (E.isLeft(result)) {
          result.left.forEach((error) => {
            reportWarn('SafePartialType', error, baseType.name);
          });
          return;
        }

        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        validatedValues[key as keyof P] = result.right;
      });

      return t.success(Object.freeze(validatedValues));
    },
    baseType.encode,
    props,
  );
}
